"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import {
  AnalyticsUserRole,
  setAnalyticsUserContext,
  trackLoginSuccess,
} from "@/lib/analytics";
import { getFriendlyAuthError } from "@/utils/authErrors";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import {
  PageWrapper,
  Container,
  Card,
  Title,
  Subtitle,
  Form,
  InputGroup,
  Label,
  Input,
  Button,
  GoogleButton,
  Divider,
  ErrorMessage,
  LinkText,
  ForgotPassword,
} from "./page.styles";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Check if user is an artisan and ensure Stripe Connect fields exist
      const userDocRef = doc(db, "accounts", userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      let userRole: AnalyticsUserRole = "consumer";

      if (userDoc.exists()) {
        const userData = userDoc.data();
        userRole = userData.isAdmin
          ? "admin"
          : userData.isArtisan
            ? "artisan"
            : "consumer";

        void setAnalyticsUserContext(userCredential.user.uid, userRole);

        // If user is an artisan and doesn't have Stripe Connect fields, add them
        if (
          userData.isArtisan &&
          (userData.stripeConnected === undefined ||
            userData.stripeConnectID === undefined)
        ) {
          await updateDoc(userDocRef, {
            stripeConnected: false,
            stripeConnectID: "",
          });
        }
      } else {
        void setAnalyticsUserContext(userCredential.user.uid, "consumer");
      }

      void trackLoginSuccess("email", userRole);

      router.push("/feed");
    } catch (err: any) {
      console.error("Login error:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Check if user is an artisan and ensure Stripe Connect fields exist
      const userDocRef = doc(db, "accounts", result.user.uid);
      const userDoc = await getDoc(userDocRef);
      let userRole: AnalyticsUserRole = "consumer";

      if (userDoc.exists()) {
        const userData = userDoc.data();
        userRole = userData.isAdmin
          ? "admin"
          : userData.isArtisan
            ? "artisan"
            : "consumer";

        void setAnalyticsUserContext(result.user.uid, userRole);

        // If user is an artisan and doesn't have Stripe Connect fields, add them
        if (
          userData.isArtisan &&
          (userData.stripeConnected === undefined ||
            userData.stripeConnectID === undefined)
        ) {
          await updateDoc(userDocRef, {
            stripeConnected: false,
            stripeConnectID: "",
          });
        }
      } else {
        void setAnalyticsUserContext(result.user.uid, "consumer");
      }

      void trackLoginSuccess("google", userRole);

      router.push("/feed");
    } catch (err: any) {
      console.error("Google login error:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <PublicHeader />
      <Container>
        <Card>
          <Title>Welcome Back!</Title>
          <Subtitle>Login to continue to Drizl</Subtitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Form onSubmit={handleEmailLogin}>
            <InputGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <ForgotPassword href="/forgot-password">
                Forgot password?
              </ForgotPassword>
            </InputGroup>

            <Button type="submit" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </Button>
          </Form>

          <Divider>
            <span>OR</span>
          </Divider>

          <GoogleButton
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <svg width="20" height="20" viewBox="0 0 20 20">
              <path
                fill="#4285F4"
                d="M19.6 10.23c0-.82-.1-1.42-.25-2.05H10v3.72h5.5c-.15.96-.74 2.31-2.04 3.22v2.45h3.16c1.89-1.73 2.98-4.3 2.98-7.34z"
              />
              <path
                fill="#34A853"
                d="M13.46 15.13c-.83.59-1.96 1-3.46 1-2.64 0-4.88-1.74-5.68-4.15H1.07v2.52C2.72 17.75 6.09 20 10 20c2.7 0 4.96-.89 6.62-2.42l-3.16-2.45z"
              />
              <path
                fill="#FBBC05"
                d="M3.99 10c0-.69.12-1.35.32-1.97V5.51H1.07A9.973 9.973 0 000 10c0 1.61.39 3.14 1.07 4.49l3.24-2.52c-.2-.62-.32-1.28-.32-1.97z"
              />
              <path
                fill="#EA4335"
                d="M10 3.88c1.88 0 3.13.81 3.85 1.48l2.84-2.76C14.96.99 12.7 0 10 0 6.09 0 2.72 2.25 1.07 5.51l3.24 2.52C5.12 5.62 7.36 3.88 10 3.88z"
              />
            </svg>
            Continue with Google
          </GoogleButton>

          <LinkText>
            Don&apos;t have an account? <Link href="/register">Sign up</Link>
          </LinkText>
        </Card>
      </Container>
      <Footer />
    </PageWrapper>
  );
}
